// Timetable: upcoming classes with their Teams links, past classes with their recordings, and for
// teachers and admins, adding, moving and cancelling classes.
import { useEffect, useLayoutEffect, useMemo, useState } from 'preact/hooks';
import { Link, useRoute } from '../router.js';
import { rpc } from '../api.js';
import { useApp } from '../state.js';
import { Icon } from '../ui/icons.js';
import { Shell } from '../ui/shell.jsx';
import { Button, Chip, Empty, ErrorBox, Field, Loading, Notice, Sheet, useRpc, useToast } from '../ui/components.jsx';
import {
  addDays, dateShort, dayLabel, istDate, istTime, minutesLabel, plural, teamsUrl, timeLabel, timeRange, todayIso, weekday, weeklyDates,
} from '../format.js';

const JOIN_EARLY = 15 * 60 * 1000; // the Join button lights up 15 minutes before the start
const LENGTHS = [[60, '1 hr'], [90, '1 hr 30 min'], [120, '2 hr'], [150, '2 hr 30 min'], [180, '3 hr']];
const DAYS = [[1, 'Mon'], [2, 'Tue'], [3, 'Wed'], [4, 'Thu'], [5, 'Fri'], [6, 'Sat'], [0, 'Sun']];
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function useNow(every = 30000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), every);
    return () => clearInterval(t);
  }, [every]);
  return now;
}

export function sessionState(s, now) {
  const start = Date.parse(s.starts_at);
  const end = Date.parse(s.ends_at);
  if (s.cancelled) return 'cancelled';
  if (now >= end) return 'ended';
  if (now >= start) return 'live';
  if (start - now <= JOIN_EARLY) return 'soon';
  return 'later';
}

// In the list, "Join" is named after its class for screen readers; on Home the card's heading does that.
// A student's tap is noted (around the class only) so the teacher's register can start from it.
function JoinButton({ s, state, label = 'Join', size, named = true }) {
  const { me } = useApp();
  const link = teamsUrl(s.teams_url);
  if (!link) return <span class="session-note">Teams link to come</span>;
  const hot = state === 'live' || state === 'soon';
  const noted = () => { if (me && me.role === 'student') rpc('record_join', { p_session_id: s.id }).catch(() => {}); };
  return (
    <a class={`btn ${hot ? 'btn-gold' : 'btn-line'}${size ? ` btn-${size}` : ''}`} href={link} target="_blank" rel="noopener" onClick={noted}
      aria-label={named ? `Join ${s.title || s.batch.name} on Teams` : undefined}>
      <Icon name="teams" /><span>{label}</span>
    </a>
  );
}

/* ---------------------------------------------------------------- Home card */
export function NextClass({ s }) {
  const now = useNow();
  const state = sessionState(s, now);
  const day = dayLabel(istDate(s.starts_at));
  return (
    <section class={`next-class${state === 'live' ? ' is-live' : ''}`} aria-labelledby="next-class-title">
      <div class="next-class-text">
        <p class="eyebrow">{state === 'live' ? 'Live now' : 'Next class'}</p>
        <h2 id="next-class-title">{s.title || s.batch.name}</h2>
        <p class="next-class-when">
          <Icon name="clock" />
          <span>{day.prefix || day.text} · {timeRange(s.starts_at, s.ends_at)}{s.title ? ` · ${s.batch.name}` : ''}</span>
        </p>
      </div>
      <div class="next-class-actions">
        <JoinButton s={s} state={state} label="Join on Teams" named={false} />
        <Link class="next-class-more" href="/timetable">Timetable <Icon name="next" /></Link>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- One class in the list */
function SessionRow({ s, now, highlight, onEdit }) {
  const state = sessionState(s, now);
  let action = null;
  if (state === 'ended') {
    action = s.recording_class_id
      ? <Link class="btn btn-line btn-sm" href={`/classes/${s.recording_class_id}`}><Icon name="play" /><span>Recording</span></Link>
      : <span class="session-note">Ended</span>;
  } else if (state !== 'cancelled') {
    action = <JoinButton s={s} state={state} size="sm" />;
  }
  // Teachers: the register, once the class is on; students: their own mark once it is taken.
  const register = s.register && (state === 'live' || state === 'ended') ? (
    <Link class={`btn btn-sm ${s.register.taken ? 'btn-line' : 'btn-navy'}`} href={`/attendance/${s.id}`}
      aria-label={`Attendance for the ${timeLabel(s.starts_at)} class on ${dateShort(istDate(s.starts_at))}${s.register.taken ? `: ${s.register.present} of ${s.register.marked} present` : ''}`}>
      <Icon name="users" /><span>{s.register.taken ? `${s.register.present}/${s.register.marked}` : 'Attendance'}</span>
    </Link>
  ) : null;
  const mine = s.my_attendance ? <Chip tone={s.my_attendance === 'absent' ? 'bad' : 'ok'}>{{ absent: 'Absent', online: 'Present online', offline: 'Present offline' }[s.my_attendance]}</Chip> : null;
  return (
    <li id={`s-${s.id}`} class={`session is-${state}${highlight ? ' is-highlight' : ''}`}>
      <div class="session-time">
        <strong>{timeLabel(s.starts_at)}</strong>
        <small>{minutesLabel(s.duration_min)}</small>
      </div>
      <div class="session-main">
        <strong class="session-title">{s.title || 'Class'}</strong>
        <span class="session-batch"><span class="subject subject-xs">{s.batch.subject || '•'}</span>{s.batch.name}</span>
        {state === 'cancelled' ? <Chip tone="bad">Cancelled</Chip> : state === 'live' ? <Chip tone="ok">Live now</Chip> : null}
        {mine}
      </div>
      <div class="session-action">
        {register}
        {action}
        {s.can_edit ? (
          <button class="icon-btn" type="button" onClick={() => onEdit(s)}
            aria-label={`Change the ${timeLabel(s.starts_at)} class on ${dateShort(istDate(s.starts_at))}`}>
            <Icon name="edit" />
          </button>
        ) : null}
      </div>
    </li>
  );
}

/* ---------------------------------------------------------------- Page */
export function Timetable() {
  const { query } = useRoute();
  const highlight = query.get('s');
  const today = todayIso();
  const [range, setRange] = useState({ from: today, to: addDays(today, 13) });
  const { data, error, loading, reload } = useRpc('my_timetable', { p_from: range.from, p_to: range.to }, [range.from, range.to]);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const now = useNow();

  const days = useMemo(() => {
    const out = [];
    for (const s of (data && data.sessions) || []) {
      const d = istDate(s.starts_at);
      if (!out.length || out[out.length - 1].date !== d) out.push({ date: d, sessions: [] });
      out[out.length - 1].sessions.push(s);
    }
    return out;
  }, [data]);

  useEffect(() => {
    if (!data || !highlight) return;
    const el = document.getElementById(`s-${highlight}`);
    if (el) el.scrollIntoView({ block: 'center' });
  }, [data, highlight]);

  const manage = (data && data.manage) || [];
  const earlier = () => setRange((r) => ({ from: addDays(r.from, -14), to: r.to > addDays(r.from, 78) ? addDays(r.from, 78) : r.to }));
  const later = () => setRange((r) => ({ from: r.to > addDays(r.from, 78) ? addDays(r.from, 14) : r.from, to: addDays(r.to, 14) }));

  return (
    <Shell title="Timetable" actions={manage.length ? (
      <span class="row-tight">
        <Link class="btn btn-line btn-sm" href="/attendance"><Icon name="users" /><span>Attendance</span></Link>
        <Button variant="gold" size="sm" icon="addcal" onClick={() => setAdding(true)}>Add classes</Button>
      </span>
    ) : null}>
      <p class="muted">Times are India time. The Join button opens the class in Microsoft Teams.</p>
      <div class="range-bar">
        <Button variant="ghost" size="sm" icon="history" onClick={earlier}>Earlier classes</Button>
        <span class="range-label">{dateShort(range.from)} to {dateShort(range.to)}</span>
      </div>
      {loading && !data ? <Loading rows={4} /> : null}
      {error ? <ErrorBox error={error} onRetry={reload} /> : null}
      {data && !days.length ? (
        <Empty icon="calendar" title="No classes in these dates"
          action={manage.length ? <Button variant="navy" icon="addcal" onClick={() => setAdding(true)}>Add classes</Button> : null}>
          {manage.length ? 'Add your batch’s classes, once or every week, with the Teams link.' : 'When the academy adds your classes, they appear here with the link to join.'}
        </Empty>
      ) : null}
      {days.map((d) => {
        const label = dayLabel(d.date);
        return (
          <section class="day" key={d.date} aria-labelledby={`d-${d.date}`}>
            <h2 class="day-head" id={`d-${d.date}`}>
              {label.prefix ? <span class="day-prefix">{label.prefix}</span> : null}
              <span>{label.text}</span>
            </h2>
            <ul class="session-list">
              {d.sessions.map((s) => <SessionRow key={s.id} s={s} now={now} highlight={s.id === highlight} onEdit={setEditing} />)}
            </ul>
          </section>
        );
      })}
      {data ? <div class="range-more"><Button variant="line" size="sm" onClick={later}>Show the next two weeks</Button></div> : null}
      {manage.length ? <AddClasses open={adding} batches={manage} onClose={() => setAdding(false)} onSaved={() => reload(true)} /> : null}
      <EditClass s={editing} open={!!editing} onClose={() => setEditing(null)} onSaved={() => reload(true)} />
    </Shell>
  );
}

/* ---------------------------------------------------------------- Teachers and admins: add classes */
function linkSettings(batch, link, saveLink) {
  const typed = (link || '').trim();
  const current = (batch && batch.teams_url) || '';
  if (!typed || typed === current) return { p_teams_url: null, p_save_link: false }; // the batch link
  return { p_teams_url: typed, p_save_link: Boolean(saveLink) };
}

function AddClasses({ open, onClose, batches, onSaved }) {
  const toast = useToast();
  const tomorrow = addDays(todayIso(), 1);
  const blank = () => {
    const only = batches.length === 1 ? batches[0] : null;
    return {
      batch: only ? only.id : '', repeat: false, date: tomorrow, from: tomorrow, until: addDays(tomorrow, 55),
      days: [], start: '19:00', length: 120, title: '', link: (only && only.teams_url) || '', saveLink: !(only && only.teams_url),
    };
  };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const batch = batches.find((b) => b.id === f.batch);

  useLayoutEffect(() => { if (open) { setErr(null); setF(blank()); } }, [open]);
  const pickBatch = (e) => {
    const b = batches.find((x) => x.id === e.currentTarget.value);
    setF({ ...f, batch: e.currentTarget.value, link: (b && b.teams_url) || '', saveLink: !(b && b.teams_url) });
  };
  useEffect(() => { // weekly: start with the weekday of the first date
    if (f.repeat && !f.days.length && f.from) setF((x) => ({ ...x, days: [weekday(x.from)] }));
  }, [f.repeat]);

  const dates = useMemo(() => (f.repeat ? weeklyDates(f.from, f.until, f.days) : (f.date ? [f.date] : [])), [f.repeat, f.date, f.from, f.until, f.days]);
  const set = (k) => (e) => setF({ ...f, [k]: e.currentTarget.type === 'checkbox' ? e.currentTarget.checked : e.currentTarget.value });
  const toggleDay = (d) => setF({ ...f, days: f.days.includes(d) ? f.days.filter((x) => x !== d) : [...f.days, d] });
  const typed = f.link.trim();
  const changed = typed && typed !== ((batch && batch.teams_url) || '');

  const summary = dates.length
    ? f.repeat
      ? `${plural(dates.length, 'class', 'classes')}: every ${DAYS.filter(([d]) => f.days.includes(d)).map(([, n]) => n).join(', ')} from ${dateShort(dates[0])} to ${dateShort(dates[dates.length - 1])}`
      : `1 class on ${DAY_NAMES[weekday(dates[0])]} ${dateShort(dates[0])}`
    : '';

  const save = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!f.batch) { setErr('Choose the batch.'); return; }
    if (!dates.length) { setErr(f.repeat ? 'Choose the days of the week, and check the dates.' : 'Choose the date.'); return; }
    setBusy(true);
    try {
      const res = await rpc('staff_add_sessions', {
        p_batch_id: f.batch, p_dates: dates, p_start: f.start, p_duration_min: Number(f.length), p_title: f.title,
        ...linkSettings(batch, f.link, f.saveLink),
      });
      toast(res.skipped ? `${plural(res.added, 'class', 'classes')} added (${res.skipped} already there)` : `${plural(res.added, 'class', 'classes')} added`);
      onSaved();
      onClose();
    } catch (ex) { setErr(ex.message); }
    setBusy(false);
  };

  return (
    <Sheet title="Add classes" open={open} onClose={onClose}>
      <form class="form" onSubmit={save}>
        <Field label="Batch">{(id) => (
          <select id={id} value={f.batch} onChange={pickBatch} required>
            {batches.length > 1 ? <option value="">Choose a batch</option> : null}
            {batches.map((b) => <option value={b.id} key={b.id}>{b.name}</option>)}
          </select>
        )}</Field>
        <div class="quick" role="group" aria-label="How often">
          <button type="button" class={`quick-btn${f.repeat ? '' : ' is-on'}`} aria-pressed={f.repeat ? 'false' : 'true'} onClick={() => setF({ ...f, repeat: false })}>One class</button>
          <button type="button" class={`quick-btn${f.repeat ? ' is-on' : ''}`} aria-pressed={f.repeat ? 'true' : 'false'} onClick={() => setF({ ...f, repeat: true })}><Icon name="repeat" /> Every week</button>
        </div>
        {f.repeat ? (
          <>
            <div class="quick days" role="group" aria-label="Days of the week">
              {DAYS.map(([d, n]) => (
                <button type="button" key={d} class={`quick-btn${f.days.includes(d) ? ' is-on' : ''}`} aria-pressed={f.days.includes(d) ? 'true' : 'false'} onClick={() => toggleDay(d)}>{n}</button>
              ))}
            </div>
            <div class="row-2">
              <Field label="From">{(id) => <input id={id} type="date" value={f.from} min={addDays(todayIso(), -60)} onInput={set('from')} required />}</Field>
              <Field label="Until">{(id) => <input id={id} type="date" value={f.until} min={f.from} onInput={set('until')} required />}</Field>
            </div>
          </>
        ) : (
          <Field label="Date">{(id) => <input id={id} type="date" value={f.date} min={addDays(todayIso(), -60)} onInput={set('date')} required />}</Field>
        )}
        <div class="row-2">
          <Field label="Starts at (India time)">{(id) => <input id={id} type="time" step="300" value={f.start} onInput={set('start')} required />}</Field>
          <Field label="Length">{(id) => (
            <select id={id} value={String(f.length)} onChange={set('length')}>
              {LENGTHS.map(([m, l]) => <option value={String(m)} key={m}>{l}</option>)}
            </select>
          )}</Field>
        </div>
        <Field label="Topic (optional)">{(id) => <input id={id} type="text" maxLength={120} value={f.title} onInput={set('title')} placeholder="Budgeting Part-2" />}</Field>
        <Field label="Teams meeting link" hint="In Teams, open the meeting and choose Copy link. One recurring meeting for the batch is easiest.">{(id) => (
          <input id={id} type="url" inputMode="url" value={f.link} onInput={set('link')} placeholder="https://teams.microsoft.com/…" />
        )}</Field>
        {changed ? (
          <label class="check"><input type="checkbox" checked={f.saveLink} onChange={set('saveLink')} />
            <span>{batch && batch.teams_url ? 'Make this the batch’s link for all its classes' : 'Use this link for all of this batch’s classes'}</span></label>
        ) : null}
        {summary ? <Notice icon="calendar">Adds {summary}, {timeLabel(`2000-01-01T${f.start}:00+05:30`)} for {minutesLabel(Number(f.length))}.</Notice> : null}
        {err ? <p class="field-error" role="alert">{err}</p> : null}
        <div class="form-actions">
          <Button type="submit" variant="navy" icon="addcal" busy={busy}>{dates.length > 1 ? `Add ${dates.length} classes` : 'Add class'}</Button>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
        </div>
      </form>
    </Sheet>
  );
}

/* ---------------------------------------------------------------- Teachers and admins: change one class */
function EditClass({ s, open, onClose, onSaved }) {
  const toast = useToast();
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState(null);
  useLayoutEffect(() => {
    if (open && s) {
      setErr(null);
      setF({ date: istDate(s.starts_at), start: istTime(s.starts_at), length: s.duration_min, title: s.title || '', link: s.own_link || '', cancelled: s.cancelled });
    }
  }, [open, s]);
  if (!s || !f) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e.currentTarget.type === 'checkbox' ? e.currentTarget.checked : e.currentTarget.value });
  const lengths = LENGTHS.some(([m]) => m === Number(f.length)) ? LENGTHS : [...LENGTHS, [Number(f.length), minutesLabel(Number(f.length))]];

  const save = async (e) => {
    e.preventDefault();
    setBusy('save'); setErr(null);
    try {
      const res = await rpc('staff_update_session', {
        p_session_id: s.id, p_date: f.date, p_start: f.start, p_duration_min: Number(f.length),
        p_title: f.title, p_teams_url: f.link.trim() || null, p_cancelled: f.cancelled,
      });
      toast(res.notice ? 'Saved. Students with reminders on get a notification.' : 'Saved');
      onSaved();
      onClose();
    } catch (ex) { setErr(ex.message); }
    setBusy('');
  };
  const remove = async () => {
    if (!confirm('Delete this class from the timetable? To tell students it isn’t happening, cancel it instead.')) return;
    setBusy('delete');
    try {
      await rpc('staff_delete_session', { p_session_id: s.id });
      toast('Class deleted');
      onSaved();
      onClose();
    } catch (ex) { setErr(ex.message); }
    setBusy('');
  };

  return (
    <Sheet title={`${s.batch.name}: change class`} open={open} onClose={onClose}>
      <form class="form" onSubmit={save}>
        <div class="row-2">
          <Field label="Date">{(id) => <input id={id} type="date" value={f.date} onInput={set('date')} required />}</Field>
          <Field label="Starts at (India time)">{(id) => <input id={id} type="time" step="300" value={f.start} onInput={set('start')} required />}</Field>
        </div>
        <div class="row-2">
          <Field label="Length">{(id) => (
            <select id={id} value={String(f.length)} onChange={set('length')}>
              {lengths.map(([m, l]) => <option value={String(m)} key={m}>{l}</option>)}
            </select>
          )}</Field>
          <Field label="Topic (optional)">{(id) => <input id={id} type="text" maxLength={120} value={f.title} onInput={set('title')} />}</Field>
        </div>
        <Field label="Teams link for this class only" hint="Leave empty to use the batch’s link.">{(id) => (
          <input id={id} type="url" inputMode="url" value={f.link} onInput={set('link')} placeholder="https://teams.microsoft.com/…" />
        )}</Field>
        <label class="check"><input type="checkbox" checked={f.cancelled} onChange={set('cancelled')} /><span>Class cancelled</span></label>
        {f.cancelled && !s.cancelled ? <Notice tone="warn" icon="alert">Students see it as cancelled. If it is in the next three days, those with reminders on get a notification.</Notice> : null}
        {err ? <p class="field-error" role="alert">{err}</p> : null}
        <div class="form-actions">
          <Button type="submit" variant="navy" busy={busy === 'save'}>Save</Button>
          <Button variant="danger" icon="trash" busy={busy === 'delete'} onClick={remove}>Delete</Button>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </div>
      </form>
    </Sheet>
  );
}
